import TAOCancellationPolicies from 'components/Common/CancellationPolicies/AlwaysOn/TAOCancellationPolicies'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'components/Luxkit/Divider'
import LineBathIcon from 'components/Luxkit/Icons/line/LineBathIcon'
import LineBedIcon from 'components/Luxkit/Icons/line/LineBedIcon'
import LineUsersAltIcon from 'components/Luxkit/Icons/line/LineUsersAltIcon'
import LineVillaIcon from 'components/Luxkit/Icons/line/LineVillaIcon'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import TextLink from 'components/Luxkit/TextLink'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { nonNullable } from 'lib/array/arrayUtils'
import { getMaxOccupancies } from 'lib/offer/occupancyUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import moment from 'moment'
import { rem } from 'polished'
import React, { useMemo } from 'react'
import { getCheapestSelectedPackageOptionWithDefault } from 'selectors/offerPage/offerPageSelectors'
import styled from 'styled-components'
import { getPropertyCategory } from 'lib/homesAndVillas/getPropertyCategory'
import ContactUSP from '../ContactUSP'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import OfferPageLabelGroup from '../OfferPageLabelGroup'
import LineMapMarkerIcon from 'components/Luxkit/Icons/line/LineMapMarkerIcon'
import OfferPagePropertyTitle from '../OfferPagePropertyTitle'

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    gap: ${rem(24)};
    grid-template-columns: max-content;
    grid-auto-columns: max-content;
    grid-auto-flow: column;
  }
`

interface Props {
  offer: App.VillaOffer;
  onViewLocationClick?: () => void;
  onViewReviewsClick: () => void;
  pkg: App.HotelPackage;
  checkIn?: string;
  duration: number;
  rooms: Array<App.Occupants>;
}

function VillasPageIntro(props: Props) {
  const { offer, onViewLocationClick, onViewReviewsClick, checkIn, pkg, rooms, duration } = props
  const { property } = offer

  const maxGuests = useMemo(() => {
    const allCapacities = offer.packages.flatMap(p => p.roomRate?.capacities ?? [])
    const max = getMaxOccupancies(allCapacities, { adults: 0 }, { maxChildAge: offer.property?.maxChildAge, maxInfantAge: offer.property?.maxInfantAge })
    return max.adults
  }, [offer])

  const propertyCategory = useMemo(() => getPropertyCategory(property.category), [property])
  const location = useMemo(() => nonNullable([offer.locationHeading, offer.locationSubheading]).join(', '), [offer])

  return (
    <Group fullWidth direction="vertical" gap={20} tabletGap={24}>
      <Group direction="vertical" gap={12}>
        <VerticalSpacer gap={16}>
          <OfferPageLabelGroup offer={offer} />
          <VerticalSpacer gap={12}>
            <BodyTextBlock
              data-testid="offer-location"
              variant="small"
              startIcon={<LineMapMarkerIcon />}
            >
              {location} • <TextLink size="small" onClick={onViewLocationClick}>View on map</TextLink>
            </BodyTextBlock>
            <OfferPagePropertyTitle propertyName={offer.property.name} />
          </VerticalSpacer>
        </VerticalSpacer>
        {isOfferRatingDisplayable(property.rating) && <OfferRating
          link
          inlineLabel
          variant="small"
          rating={property.rating}
          onReviewSubTitleClick={onViewReviewsClick}
        />}
      </Group>
      <Group direction="vertical" gap={16}>
        <Divider kind="primary" />
        <SummaryGrid>
          <BodyTextBlock variant="medium" startIcon={<LineVillaIcon />}>
            {propertyCategory}
          </BodyTextBlock>
          <BodyTextBlock variant="medium" startIcon={<LineUsersAltIcon />}>
            Up to {pluralizeToString('guest', maxGuests)}
          </BodyTextBlock>
          <BodyTextBlock variant="medium" startIcon={<LineBedIcon />}>
            {pluralizeToString('bedroom', offer.property.bedrooms)}
          </BodyTextBlock>
          <BodyTextBlock variant="medium" startIcon={<LineBathIcon />}>
            {pluralizeToString('bathroom', offer.property.bathrooms)}
          </BodyTextBlock>
        </SummaryGrid>
        <Divider kind="primary" />
      </Group>
      {checkIn && <CSSBreakpoint max="tablet">
        <TAOCancellationPolicies
          offerId={offer.id}
          useDynamicCancellationPolicies={offer.property.useDynamicCancellationPolicies && !pkg.ignoreDynamicCancellationPolicy}
          timezoneOffset={offer.property.timezoneOffset}
          uniqueKey={pkg.uniqueKey}
          checkInDate={checkIn}
          checkOutDate={checkIn ? moment(checkIn).add(duration, 'days').format(ISO_DATE_FORMAT) : undefined}
          occupants={rooms}
          cancellationPolicy={pkg.roomRate.cancellationPolicy}
          hideExcludesFlights
          offerType={offer.type}
        />
      </CSSBreakpoint>}
      <div>
        <LargeContentToggle maxHeight={120} showChevron showLessText="Read less" showMoreText="Read more">
          <MarkdownRender type="compact" content={offer.description} />
        </LargeContentToggle>
      </div>
      <ContactUSP />
    </Group>
  )
}

export default withOfferPageAndReduxState((state, appState) => {
  return {
    pkg: getCheapestSelectedPackageOptionWithDefault(appState, state).package,
    checkIn: state.checkInDate,
    duration: state.duration,
    rooms: state.rooms,
  }
})(VillasPageIntro)
